import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { STATUS_META, STATUS_ORDER } from "@/lib/constants";
import { updateSettings } from "@/lib/api";

export const PaymentMethodsDialog = ({ open, onOpenChange, settings, onSaved }) => {
  const [methods, setMethods] = useState([]);
  const [currency, setCurrency] = useState("USD");
  const [newM, setNewM] = useState("");

  useEffect(() => {
    if (settings) {
      setMethods([...settings.payment_methods]);
      setCurrency(settings.currency || "USD");
    }
  }, [settings, open]);

  const add = () => {
    const v = newM.trim();
    if (!v) return;
    if (methods.includes(v)) {
      toast.error("Esa forma de pago ya existe");
      return;
    }
    setMethods([...methods, v]);
    setNewM("");
  };

  const save = async () => {
    try {
      const s = await updateSettings({ ...settings, payment_methods: methods, currency });
      toast.success("Formas de pago actualizadas");
      onSaved?.(s);
      onOpenChange(false);
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="manage-payment-methods-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Formas de Pago</DialogTitle>
          <DialogDescription>Añade o elimina métodos según tu criterio.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex flex-wrap gap-2">
            {methods.map((m) => (
              <span
                key={m}
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2.5 py-1.5 text-sm text-slate-700"
              >
                {m}
                <ConfirmDialog
                  title="¿Eliminar forma de pago?"
                  description={`Se quitará "${m}" de la lista. Guarda para aplicar el cambio.`}
                  testId={`confirm-remove-payment-${m}`}
                  onConfirm={() => setMethods(methods.filter((x) => x !== m))}
                  trigger={
                    <button
                      data-testid={`remove-payment-${m}`}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  }
                />
              </span>
            ))}
            {methods.length === 0 && (
              <p className="text-sm text-slate-400">Sin formas de pago.</p>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Input
              data-testid="new-payment-method-input"
              placeholder="Ej. Zelle, Efectivo, MercadoPago"
              value={newM}
              onChange={(e) => setNewM(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
            <Button variant="outline" onClick={add} data-testid="add-payment-method-button">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid gap-1.5 pt-2 border-t border-slate-100">
            <Label className="pt-2">Moneda (para el historial de pagos)</Label>
            <Input
              data-testid="currency-input"
              placeholder="Ej. USD, MXN, EUR"
              value={currency}
              maxLength={3}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={save} data-testid="save-payment-methods-button">
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const StatusLabelsDialog = ({ open, onOpenChange, settings, onSaved }) => {
  const [labels, setLabels] = useState({});

  useEffect(() => {
    if (settings) setLabels({ ...settings.status_labels });
  }, [settings, open]);

  const save = async () => {
    try {
      const s = await updateSettings({ ...settings, status_labels: labels });
      toast.success("Etiquetas del semáforo actualizadas");
      onSaved?.(s);
      onOpenChange(false);
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="status-label-editor-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Etiquetas del Semáforo</DialogTitle>
          <DialogDescription>Define el significado de cada color.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {STATUS_ORDER.map((key) => {
            const m = STATUS_META[key];
            return (
              <div key={key} className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full shrink-0" style={{ backgroundColor: m.hex }} />
                <Label className="w-16 text-sm text-slate-500">{m.name}</Label>
                <Input
                  data-testid={`status-label-input-${key}`}
                  value={labels[key] || ""}
                  onChange={(e) => setLabels({ ...labels, [key]: e.target.value })}
                />
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={save} data-testid="save-status-labels-button">
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
